import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { adminService } from '../../../services/adminService';

const SystemSettingsCard = ({ title, description, icon, iconColor, settingType }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#FFD700');
  const [watermarkQuality, setWatermarkQuality] = useState(80);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load settings from database
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settings = await adminService?.getSettings();
      
      if (settings?.primary_color) {
        setPrimaryColor(settings?.primary_color?.color || '#FFD700');
      }
      
      if (settings?.watermark_quality) {
        setWatermarkQuality(settings?.watermark_quality?.quality || 80);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      
      switch (settingType) {
        case 'colors': await adminService?.updateSetting('primary_color', {
            color: primaryColor,
            updated_at: new Date()?.toISOString()
          });
          break;
        
        case 'watermark': await adminService?.updateSetting('watermark_quality', {
            quality: parseInt(watermarkQuality),
            updated_at: new Date()?.toISOString()
          });
          break;
      }
      
      setIsExpanded(false);
    } catch (err) {
      setError(err?.message || 'Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  const renderSettingContent = () => {
    switch (settingType) {
      case 'colors':
        return (
          <div className="space-y-4">
            <Input
              type="text"
              label="Cor Primária"
              description="Código hexadecimal (ex: #FFD700)"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e?.target?.value)}
              placeholder="#FFD700"
            />
            <div 
              className="w-full h-12 rounded-lg border-2 border-border"
              style={{ backgroundColor: primaryColor }}
            />
          </div>
        );
      
      case 'watermark':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Qualidade da Marca D'água: {watermarkQuality}%
              </label>
              <input
                type="range"
                min="50"
                max="100"
                value={watermarkQuality}
                onChange={(e) => setWatermarkQuality(e?.target?.value)}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>Baixa</span>
                <span>Alta</span>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 glow-primary transition-smooth hover:glow-primary-hover">
      <div className="flex items-start gap-4 mb-4">
        <div 
          className="w-10 h-10 md:w-12 md:h-12 rounded-md flex items-center justify-center flex-shrink-0 glow-primary"
          style={{ backgroundColor: `${iconColor}20` }}
        >
          <Icon name={icon} size={24} color={iconColor} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-base md:text-lg font-heading font-bold text-foreground mb-1">
            {title}
          </h4>
          <p className="text-xs md:text-sm text-muted-foreground line-clamp-2">
            {description}
          </p>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-2 rounded-md hover:bg-muted transition-smooth"
        >
          <Icon 
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
            size={20} 
            color="var(--color-foreground)" 
          />
        </button>
      </div>

      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-border">
          {error && (
            <div className="mb-4 p-2 bg-red-500/10 border border-red-500 rounded text-xs text-red-500">
              {error}
            </div>
          )}
          {renderSettingContent()}
          <div className="flex gap-2 mt-4">
            <Button 
              variant="default" 
              size="sm"
              iconName="Save"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsExpanded(false)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettingsCard;